import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Button, Dialog, Stack, vars } from '@etg/wings';
import { useProperty, useTranslation, useSiteContext } from '@eti/providers';
import { css } from '@eti/styles';
import { AgeType, BoundNew, ID } from '@eti/types';
import { BOOKING, RENTALCARS } from '../../../../constants/partnerConstants';
import { resolveDeepLinkToBooking } from '../../../../common/deeplinking/booking';

const messageStyles = css`
  font-size: 1rem;
  line-height: 1.5;
`;

const partnersStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: ${breakpoints._560}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }
`;

const partnerStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
  display: flex;
  position: relative;

  @media (max-width: ${breakpoints._560}) {
    &::after {
      border-color: ${vars.colors.focusRing};
      border-radius: 6px;
      border-style: solid;
      border-width: 2px;
      content: '';
      height: calc(100% + 10px);
      left: -5px;
      opacity: 0;
      position: absolute;
      top: -5px;
      transition: opacity 0.2s ease-in-out;
      width: calc(100% + 10px);
      z-index: -1;
    }

    &:focus-within::after {
      opacity: 1;
    }
  }

  @media (min-width: ${breakpoints._560}) {
    flex-direction: column;
  }
`;

const partnerImageStyles = css`
  border-bottom-left-radius: 3px;
  border-top-left-radius: 3px;
  display: block;
  object-fit: cover;
  position: relative;
  width: 50%;

  @media (min-width: ${breakpoints._560}) {
    border-bottom-left-radius: 0;
    border-top-right-radius: 3px;
    width: 100%;
  }
`;

const logoStyles = css`
  align-self: flex-start;
  height: 14px;
`;

const buttonStyles = css`
  @media (max-width: ${breakpoints._560}) {
    height: 0;
    opacity: 0;
    padding: 0;
    position: static;
    width: 100%;

    &::after {
      content: '';
      inset: 0;
      pointer-events: all;
      position: absolute;
    }
  }
`;

const partnerContentStyles = css`
  justify-content: center;
  padding: 16px;

  @media (min-width: ${breakpoints._560}) {
    height: 100%;
  }
`;

const partnerLogoLinkStyles = css`
  @media (min-width: ${breakpoints._560}) {
    margin-top: auto;
  }
`;

const PartnerLogo = ({ partner }) => {
  const [logo, setLogo] = useState(<div />);

  useEffect(() => {
    let isMounted = true;
    const loadLogo = async () => {
      try {
        const { ReactComponent } = await import(
          `@eti/assets/logos/partners/${partner.toLowerCase()}.svg`
        );

        if (isMounted) {
          setLogo(<ReactComponent className={logoStyles} role="img" />);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Something went wrong when loading the logotype for ${partner}: ${error}`);
      }
    };

    loadLogo();

    return () => {
      isMounted = false;
    };
  }, [partner]);

  return logo;
};

PartnerLogo.propTypes = {
  partner: PropTypes.oneOf([BOOKING, RENTALCARS]),
};

const HotelsAndRentalCarsModal = ({ bounds, travelers }) => {
  const { siteCurrency, language } = useSiteContext();
  const { t } = useTranslation();
  const { p } = useProperty();
  const [showModal, setShowModal] = useState(false);
  const [imagery, setImagery] = useState({});

  const closeModal = () => setShowModal(false);
  const buttonLabel = t('Order.HotelAndCarModal.CTA.Label');
  const hotelUrl = resolveDeepLinkToBooking(
    bounds,
    language.code,
    siteCurrency.code,
    travelers,
    'conf_page_pop_up',
  );

  useEffect(() => {
    const loadImagery = async () => {
      try {
        const [rentalCarsImage, hotelsImage] = await Promise.all([
          await import('./assets/car-rental.jpg').then((imp) => imp.default),
          await import('./assets/book-hotel.jpg').then((imp) => imp.default),
        ]);

        setImagery({ rentalCarsImage, hotelsImage });
        setShowModal(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `Something went wrong when loading the hotels & rental cars offer images: ${error}`,
        );
      }
    };

    loadImagery();
  }, []);

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="HotelAndCarOverlay"
      isOpen={showModal}
      onDismiss={closeModal}
      title={t('Order.HotelAndCarModal.Title')}
    >
      <Stack spacing={{ [breakpoints._0]: 24, [breakpoints._560]: 16 }}>
        <p className={messageStyles}>{t('Order.HotelAndCarModal.Text')}</p>
        <div className={partnersStyles}>
          <div className={partnerStyles}>
            <img alt="" className={partnerImageStyles} src={imagery.rentalCarsImage} />
            <Stack
              className={partnerContentStyles}
              data-testid="HotelAndCarOverlay-card-rentalcars"
            >
              <span>{t('Order.HotelAndCarModal.RentalCars.Label')}</span>
              <Stack
                className={partnerLogoLinkStyles}
                spacing={{ [breakpoints._0]: 0, [breakpoints._560]: 8 }}
              >
                <PartnerLogo partner={RENTALCARS} />
                <Button
                  as="a"
                  className={buttonStyles}
                  href={p('Order.CarModal.Url')}
                  rel="noopener"
                  target="_blank"
                >
                  {buttonLabel}
                </Button>
              </Stack>
            </Stack>
          </div>
          <div className={partnerStyles}>
            <img alt="" className={partnerImageStyles} src={imagery.hotelsImage} />
            <Stack className={partnerContentStyles} data-testid="HotelAndCarOverlay-card-hotel">
              <span>{t('Order.HotelAndCarModal.Booking.Label')}</span>
              <Stack className={partnerLogoLinkStyles} spacing={8}>
                <PartnerLogo partner={BOOKING} />
                <Button
                  as="a"
                  className={buttonStyles}
                  data-testid="book-now-button"
                  href={hotelUrl}
                  rel="noopener"
                  target="_blank"
                >
                  {buttonLabel}
                </Button>
              </Stack>
            </Stack>
          </div>
        </div>
      </Stack>
    </Dialog>
  );
};

HotelsAndRentalCarsModal.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: AgeType,
      id: ID,
      name: PropTypes.string,
    }),
  ),
};
export default HotelsAndRentalCarsModal;
